/** @jsxImportSource @emotion/react */
import React from "react";
import {
  ProductUsecase,
  useProductDispatch,
  useProductState,
} from "../../../usecases/Product";
import { SettingUsecase, useSettingDispatch } from "../../../usecases/Setting";
import { useAuthState } from "../../../usecases/Auth";
import { RouteInfos } from "../../../common/routeInfo";
import { GetListParams } from "../../../services/Product/product.repository.d";
import { Images } from "../../../common/images";
import {
  DataTable,
  Toolbox,
  useColumnVisibility,
  useInlineEdit,
  ColumnConfig,
  DisplayColumns,
  ThumbnailSize,
} from "../../../components/DataTable";
import { LayoutContent } from "../../../components/LayoutContent";
import { LocalStorage } from "../../../modules/LocalStorage";

const PRODUCT_COLUMN_CONFIGS: ColumnConfig[] = [
  { key: "productThumbnailUrl", title: "Thumbnail", type: "thumbnail" },
  { key: "productKey", title: "Key", type: "plain", sortable: true },
  { key: "name", title: "Name", type: "text", sortable: true, editable: true },
  {
    key: "productPrice",
    title: "Price",
    type: "number",
    sortable: true,
    editable: true,
  },
  {
    key: "productWidth",
    title: "Width",
    type: "number",
    sortable: true,
    editable: true,
  },
  {
    key: "productHeight",
    title: "Height",
    type: "number",
    sortable: true,
    editable: true,
  },
  {
    key: "productDepth",
    title: "Depth",
    type: "number",
    sortable: true,
    editable: true,
  },
  {
    key: "productCategories",
    title: "Category",
    type: "arrayString",
    sortable: true,
    editable: true,
    filterable: true,
  },
  {
    key: "productRooms",
    title: "Room",
    type: "arrayString",
    sortable: true,
    filterable: true,
  },
  {
    key: "productSurfaces",
    title: "Surface",
    type: "arrayString",
    sortable: true,
    filterable: true,
  },
  {
    key: "productManufacturer",
    title: "Manufacturer",
    type: "text",
    sortable: true,
    editable: true,
  },
  {
    key: "productFileName",
    title: "File Name",
    type: "text",
    sortable: true,
    editable: true,
  },
  { key: "homeNames", title: "Homes", type: "arrayString", sortable: true },
  {
    key: "updatedByFullName",
    title: "Modified By",
    type: "text",
    sortable: true,
  },
  { key: "updatedAt", title: "Date Modified", type: "date", sortable: true },
  { key: "isActive", title: "Status", type: "status", editable: true },
];

interface ProductComponentProps {}

const ProductComponent: React.FC<ProductComponentProps> = () => {
  const productState = useProductState();
  const productDispatch = useProductDispatch();
  const settingDispatch = useSettingDispatch();
  const authState = useAuthState();

  const [filter, setFilter] = React.useState<GetListParams>({});

  React.useEffect(() => {
    async function asyncLoadData() {
      if (productDispatch && filter.tenantId) {
        const productUsecase = new ProductUsecase(productDispatch);
        await productUsecase.getList(filter);
      }
    }
    asyncLoadData();
  }, [productDispatch, filter]);

  // Page-specific: set breadcrumb menu detail from tenant
  React.useEffect(() => {
    const tenantInfo = LocalStorage.getTenantInfo();
    const settingUsecase = new SettingUsecase(settingDispatch);
    settingUsecase.setMenuDetail({
      id: tenantInfo.tenantId,
      title: tenantInfo.tenantName,
      routeInfo: RouteInfos.HomeBuilderProducts,
    });
    setFilter((prev) => ({ ...prev, tenantId: tenantInfo.tenantId }));
  }, [settingDispatch]);

  const handleSearch = React.useCallback((keyword: string) => {
    setFilter((prev) => ({ ...prev, keyword }));
  }, []);

  const handleSave = React.useCallback(
    async (items: Array<Record<string, unknown>>) => {
      if (productDispatch) {
        const productUsecase = new ProductUsecase(productDispatch);
        await productUsecase.updateList({
          userId: authState.userInfo?.username,
          items,
        });
      }
    },
    [authState.userInfo?.username, productDispatch],
  );

  const productUsecase = React.useMemo(
    () => (productDispatch ? new ProductUsecase(productDispatch) : undefined),
    [productDispatch],
  );

  const getDisplayColumns = React.useCallback(
    (): DisplayColumns =>
      (productUsecase?.getDisplayColumns() ?? {}) as DisplayColumns,
    [productUsecase],
  );

  const setDisplayColumns = React.useCallback(
    (cols: DisplayColumns) => productUsecase?.setDisplayColumns(cols as never),
    [productUsecase],
  );

  const [thumbnailSize, setThumbnailSize] =
    React.useState<ThumbnailSize>("medium");

  const visibility = useColumnVisibility(
    PRODUCT_COLUMN_CONFIGS,
    getDisplayColumns,
    setDisplayColumns,
  );

  const edit = useInlineEdit(
    PRODUCT_COLUMN_CONFIGS,
    productState.data,
    "productKey",
    handleSave,
  );

  return (
    <LayoutContent
      title="PRODUCTS"
      iconPath={Images.PRODUCT_BLACK}
      toolbar={
        <Toolbox>
          <Toolbox.Search onSearch={handleSearch} placeholder="Find..." />
          <Toolbox.ColumnToggle {...visibility} />
          <Toolbox.ThumbnailResize
            value={thumbnailSize}
            onChange={setThumbnailSize}
          />
          <Toolbox.Spacer />
          <Toolbox.EditActions
            isEditing={edit.isEditing}
            onEdit={edit.onButtonEditClick}
            onCancel={edit.onButtonCancelClick}
          />
        </Toolbox>
      }
    >
      <DataTable
        rowKey="productKey"
        dataSource={productState.data}
        columnConfigs={PRODUCT_COLUMN_CONFIGS}
        columnToggle
        editable
        pagination
        thumbnailSize={thumbnailSize}
        visibility={visibility}
        edit={edit}
      />
    </LayoutContent>
  );
};

export const Product = React.memo(ProductComponent);
